import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  AuthContext,
  Icon,
  Loader,
  Logo,
  useAPI,
  useNavigate,
  ViewContext
} from "@/components/lib";
import axios from "axios";
import { State } from "country-state-city";
import { DropCard } from "@/components/dropCard/dropCard";
import ImagesUpload from "@/components/form/file/newFile";
import defaultPlayerImage from "@/images/default_american_football.png";
import Styles from "@/css/mainStyles";

export default function EditPlayer() {
  const navigate = useNavigate();
  const viewContext = useContext<any>(ViewContext);
  const authContext = useContext<any>(AuthContext);
  const { user } = authContext;
  const { data: player, loading: playerLoading } = useAPI(
    `/api/player/profile?profileName=${user?.profileName}`
  );
  const { data: coachesData } = useAPI(`/api/coach`);
  const [loading, setLoading] = useState(false);
  const [previewProfile, setPreviewProfile] = useState("");
  const [formError, setFormError] = useState("");
  const [imageError, setImageError] = useState<boolean>(false);

  const filesURL = "https://ghs-general.s3.us-east-1.amazonaws.com/";

  const gallery_images =
    player?.gallery?.length > 0
      ? player.gallery.map((file: any) => ({
          type: file.format,
          url: filesURL + file.url
        }))
      : [];

  const coachesOptions =
    coachesData?.length > 0
      ? coachesData?.map((coach: any) => ({
          label: coach.first_name + " " + coach.last_name,
          value: coach.id
        }))
      : [];

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm();

  useEffect(() => {
    if (player) {
      setValue(
        "profile_pic",
        player.profile_pic
          ? { type: "image", url: filesURL + player.profile_pic }
          : null
      );
      player.profile_pic && setPreviewProfile(filesURL + player.profile_pic);
      setValue("gallery", player.gallery);
      setValue("first_name", player.first_name);
      setValue("last_name", player.last_name);
      setValue("email", player.email);
      setValue("shirt", player.shirt);
      setValue("position", player.info?.position);
      setValue("class", player.info?.class);
      setValue("heightFeet", player.info?.height?.feet || NaN);
      setValue("heightInches", player.info?.height?.inches || NaN);
      setValue("weight", player.info?.weight || "");
      setValue("fourty", player.info?.fourty || "");
      setValue("agility", player.info?.agility || "");
      setValue("jumpFeet", player.info?.jump?.feet || NaN);
      setValue("jumpInches", player.info?.jump?.inches || NaN);
      setValue("vertical", player.info?.vertical?.feet || NaN);
      setValue("bench", player.info?.bench || "");
      setValue("squat", player.info?.squat || "");
      setValue("clean", player.info?.clean || "");
      setValue("gpa", player.info?.gpa || "");
      setValue("sat", player.info?.sat || "");
      setValue("act", player.info?.act || "");
      setValue("recognition", player.info?.recognition || "");
      setValue("school", player.info?.school);
      setValue("city", player.info?.city);
      setValue("state", player.info?.state);
      setValue("coach", player.info?.coach_id);
      setValue("offers", player.info?.offers || "");
      setValue("twitter_id", player.twitter_id);
      setValue("hudl", player.hudl);
    }
  }, [player, setValue]);

  const positionOptions = [
    { value: "QB", label: "QB" }, // secondLabel: "Quarterback" },
    { value: "RB", label: "RB" }, // secondLabel: "Running Back" },
    { value: "SLOT", label: "SLOT" }, // secondLabel: "Slot Receiver" },
    { value: "WR", label: "WR" }, // secondLabel: "Wide Receiver" },
    { value: "TE", label: "TE" }, // secondLabel: "Tight End" },
    { value: "OL", label: "OL" }, // secondLabel: "Offensive Lineman" },
    { value: "DL", label: "DL" }, // secondLabel: "Defensive Lineman" },
    { value: "OLB", label: "OLB" }, // secondLabel: "Outside Linebacker" },
    { value: "ILB", label: "ILB" }, // secondLabel: "Inside Linebacker" },
    { value: "LB", label: "LB" }, // secondLabel: "Linebacker" },
    { value: "DE", label: "DE" }, // secondLabel: "Defensive End" },
    { value: "SAF", label: "SAF" }, // secondLabel: "Safety" },
    { value: "DB", label: "DB" }, // secondLabel: "Defensive Back" },
    { value: "K/P", label: "K/P" }, // secondLabel: "Kicker/Punter" },
    { value: "ATH", label: "ATH" } // secondLabel: "Athlete" }
  ];

  const states = State.getStatesOfCountry("US");
  let statesOptions = [];
  for (const state of states)
    statesOptions.push({ value: state.name, label: state.name });

  const handleRemoveFile = async (file: any, index: number) => {
    try {
      if (!file.url.includes("blob")) {
        const fileURLDelete = file.url.split(filesURL)[1];
        await axios({
          method: "delete",
          url: "/api/player/gallery/remove/" + fileURLDelete
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const validateFileSize = (blob, maxSizeMB, errorMessage) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (blob.size > maxSizeBytes) {
      setFormError(errorMessage);
      viewContext.notification.show(errorMessage, "error");
      throw new Error(errorMessage);
    }
    return true;
  };

  const onSubmit = async (data: any) => {
    setFormError("");
    const height = { feet: data.heightFeet, inches: data.heightInches };
    const jump = { feet: data.jumpFeet, inches: data.jumpInches };

    const info = {
      height,
      weight: data.weight,
      position: data.position,
      class: data.class,
      fourty: data.fourty,
      agility: data.agility,
      jump,
      vertical: { feet: data.vertical },
      bench: data.bench,
      squat: data.squat,
      clean: data.clean,
      gpa: data.gpa,
      sat: data.sat,
      act: data.act,
      recognition: data.recognition,
      school: data.school,
      city: data.city,
      state: data.state,
      coach_id: data.coach,
      offers: data.offers
    };

    const formPlayerData = new FormData();
    formPlayerData.append("first_name", data.first_name);
    formPlayerData.append("last_name", data.last_name);
    formPlayerData.append("email", data.email);
    formPlayerData.append("shirt", data.shirt);
    formPlayerData.append("info", JSON.stringify(info));
    formPlayerData.append("twitter_id", data.twitter_id);
    formPlayerData.append("hudl", data.hudl);

    if (data.profile_pic?.url?.includes("blob")) {
      try {
        const response = await fetch(data.profile_pic.url);
        const blob = await response.blob();

        if (
          !validateFileSize(
            blob,
            5,
            "Profile Picture can’t be bigger than 5MB."
          )
        ) {
          return;
        }

        const file = new File([blob], "GHS_player_image", {
          type: blob.type
        });
        formPlayerData.append("profile_pic", file);
      } catch (error) {
        console.error(error);
      }
    }

    if (data.gallery?.length > 0) {
      if (data.gallery.length > 8) {
        return setFormError("Maximum of 8 files allowed.");
      }
      for (const file of data.gallery) {
        if (file.url.includes("blob")) {
          try {
            const response = await fetch(file.url);
            const blob = await response.blob();
            const originalFileName = file.url.split("/").pop();
            const maxSizeMB = file.type === "video" ? 100 : 10;

            if (
              !validateFileSize(
                blob,
                maxSizeMB,
                file.type === "video"
                  ? "A video can’t be bigger than 100MB."
                  : "Gallery Picture can’t be bigger than 10MB."
              )
            )
              return;

            const newFile = new File([blob], originalFileName, {
              type: blob.type
            });
            formPlayerData.append(file.type, newFile);
          } catch (error) {
            console.error(error);
          }
        }
      }
    }

    const editurl = `/api/player/${user.id}`;

    try {
      setLoading(true);
      const res = await axios({
        method: "patch",
        url: editurl,
        data: formPlayerData,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      viewContext.notification.show("Player updated", "success");
      res.data?.data?.profile_name &&
        authContext.update({ profileName: res.data.data.profile_name });
      setTimeout(() => {
        navigate(`/${res.data?.data?.profile_name || user.profileName}`);
      }, 3000);
    } catch (error) {
      console.error(error);
      const message =
        error.response?.data?.errors?.length > 0
          ? error.response.data.errors[0].message
          : error.message;
      viewContext.notification.show(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileImageChange = (event) => {
    const file = event.target.files[0];
    const fileURL = URL.createObjectURL(file);

    if (file) {
      setPreviewProfile(fileURL);
      setValue("profile_pic", { type: "image", url: fileURL });
      setImageError(false);
    } else {
      event.target.value = "";
    }
  };

  if (playerLoading)
    return (
      <div className="w-full flex justify-center pt-10">
        <Loader />
      </div>
    );

  if (!player) {
    return (
      <div className="text-center text-xl w-full text-white mt-4">
        Player not found
      </div>
    );
  } else {
    return (
      <div className="pb-10 md:flex gap-4">
        <div className="md:w-[48%]">
          <div className="relative w-full overflow-hidden shadow-lg min-h-[300px]">
            {!imageError && previewProfile ? (
              <img
                src={previewProfile}
                alt="Grapevine High School"
                className="w-full h-[450px] md:h-[550px] lg:h-[600px] object-cover overflow-hidden"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <img
                  src={defaultPlayerImage}
                  alt="Grapevine High School"
                  className="w-full lg:w-3/5 h-[300px] md:h-[400px] lg:h-full"
                />
              </div>
            )}

            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(7, 16, 27, 0) 50%, #07101B 100%)"
              }}
            />
            <a
              href={"/" + user.profileName}
              className="rounded-full bg-white p-2 absolute top-8 left-6"
            >
              <Icon
                size={20}
                image="arrow-left"
                className="text-blue-600 w-6 h-6"
              />
            </a>
            <div className="w-3/4 m-auto flex items-center justify-center h-3/4 absolute top-0 right-0 left-0 bottom-0 opacity-85 md:opacity-0 hover:opacity-85 cursor-pointer">
              <div className="flex gap-2 bg-white rounded p-2">
                <label className="cursor-pointer">
                  <Icon image="edit" size={20} />
                  <input
                    type="file"
                    className="hidden"
                    id="profile_pic"
                    accept="image/*"
                    {...register("profile_pic")}
                    onChange={handleProfileImageChange}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setValue("profile_pic", "");
                    setPreviewProfile("");
                  }}
                >
                  <Icon image="trash" size={20} />
                </button>
              </div>
            </div>
          </div>
          <div className="md:block p-4 hidden mt-2">
            <ImagesUpload
              maxFiles={8}
              onChange={(files: any) => setValue("gallery", files)}
              defaultFiles={gallery_images}
              onDelete={(file, index) => handleRemoveFile(file, index)}
              accept="image/*, video/*"
            />
          </div>
        </div>
        <div className="space-y-4 font-bold px-4 md:w-[52%]">
          <h1 className="text-white text-2xl md:text-4xl font-bold ml-5 mt-2">
            Edit your profile
          </h1>
          <form
            className="flex flex-col gap-2 mt-4 px-6"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex justify-between items-center gap-4">
              <div className="flex flex-col w-full">
                <label className={Styles.label}>First Name</label>
                <input
                  placeholder="First Name"
                  type="text"
                  className={Styles.input}
                  {...register("first_name", { required: true })}
                  style={errors.first_name && { border: "1px solid red" }}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>Last Name</label>
                <input
                  placeholder="Last Name"
                  type="text"
                  className={Styles.input}
                  {...register("last_name", { required: true })}
                  style={errors.last_name && { border: "1px solid red" }}
                />
              </div>
            </div>

            <div className="flex flex-col w-full">
              <label className={Styles.label}>Jersey Number</label>
              <input
                placeholder="1"
                min={0}
                max={99}
                type="number"
                className={Styles.input}
                {...register("shirt", { required: true })}
                style={errors.shirt && { border: "1px solid red" }}
              />
            </div>

            <div className="flex justify-between items-center gap-4">
              <div className="flex flex-col w-full">
                <label className={Styles.label}>Position</label>

                <select
                  className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300 relative z-10"
                  {...register("position")}
                  style={errors.position && { border: "1px solid red" }}
                >
                  <option value="">Select</option>
                  {positionOptions.map((position) => (
                    <option key={position.value} value={position.value}>
                      {position.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>Class</label>
                <input
                  placeholder="2030"
                  min={1}
                  type="number"
                  className={Styles.input}
                  {...register("class")}
                  style={errors.class && { border: "1px solid red" }}
                />
              </div>
            </div>

            <div className="flex justify-between items-center gap-4">
              <div className="flex flex-col w-3/5">
                <p className="text-sm lg:text-lg font-medium text-white ml-2 mb-2">
                  Height
                </p>
                <div className="flex justify-between items-center gap-4">
                  <input
                    placeholder="Feet"
                    type="number"
                    min={0}
                    max={9}
                    className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
                    {...register("heightFeet")}
                    style={errors.heightFeet && { border: "1px solid red" }}
                  />
                  <input
                    placeholder="Inches"
                    type="number"
                    min={0}
                    max={11}
                    className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
                    {...register("heightInches")}
                    style={errors.heightInches && { border: "1px solid red" }}
                  />
                </div>
              </div>
              <div className="flex flex-col w-2/5">
                <label className="text-sm lg:text-lg font-medium text-white ml-2 mb-2">
                  Weight (lbs)
                </label>
                <input
                  placeholder="Weight"
                  type="text"
                  className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
                  {...register("weight")}
                  style={errors.weight && { border: "1px solid red" }}
                />
              </div>
            </div>

            <input type="hidden" {...register("gallery")} />
            <DropCard
              title="Strength & Speed"
              className="flex flex-col gap-2 py-2"
            >
              <div className="flex flex-col w-full">
                <label className={Styles.label}>Pro Agility</label>
                <input
                  placeholder=""
                  min={1}
                  step={0.01}
                  type="number"
                  className={Styles.input}
                  {...register("agility")}
                  style={errors.agility && { border: "1px solid red" }}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>40 Time</label>
                <input
                  placeholder=""
                  min={1}
                  step={0.001}
                  type="number"
                  className={Styles.input}
                  {...register("fourty")}
                  style={errors.fourty && { border: "1px solid red" }}
                />
              </div>
              <div className="flex flex-col w-full">
                <p className={Styles.label}>Broad Jump</p>
                <div className="flex justify-between items-center gap-4">
                  <input
                    placeholder="Feet"
                    type="number"
                    min={0}
                    max={9}
                    className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
                    {...register("jumpFeet")}
                    style={errors.jumpFeet && { border: "1px solid red" }}
                  />
                  <input
                    placeholder="Inches"
                    type="number"
                    min={0}
                    max={11}
                    className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
                    {...register("jumpInches")}
                    style={errors.jumpInches && { border: "1px solid red" }}
                  />
                </div>
              </div>
              <div className="flex flex-col w-full">
                <p className={Styles.label}>Vertical</p>
                <input
                  placeholder="Inches"
                  type="number"
                  min={0}
                  step={0.01}
                  className={Styles.input}
                  {...register("vertical")}
                  style={errors.vertical && { border: "1px solid red" }}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>Bench Max</label>
                <input
                  placeholder=""
                  min={1}
                  type="number"
                  className={Styles.input}
                  {...register("bench")}
                  style={errors.bench && { border: "1px solid red" }}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>Squat Max</label>
                <input
                  placeholder=""
                  min={1}
                  type="number"
                  className={Styles.input}
                  {...register("squat")}
                  style={errors.squat && { border: "1px solid red" }}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>Clean Max</label>
                <input
                  placeholder=""
                  min={1}
                  type="number"
                  className={Styles.input}
                  {...register("clean")}
                  style={errors.clean && { border: "1px solid red" }}
                />
              </div>
            </DropCard>
            <DropCard title="Academics" className="flex flex-col gap-2 py-2">
              <div className="flex flex-col w-full">
                <label className={Styles.label}>GPA</label>
                <input
                  placeholder=""
                  min={0}
                  max={6}
                  step={0.1}
                  type="number"
                  className={Styles.input}
                  {...register("gpa")}
                  style={errors.gpa && { border: "1px solid red" }}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>SAT Score</label>
                <input
                  placeholder=""
                  min={1}
                  type="number"
                  className={Styles.input}
                  {...register("sat")}
                  style={errors.sat && { border: "1px solid red" }}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>ACT Score</label>
                <input
                  placeholder=""
                  min={1}
                  type="number"
                  className={Styles.input}
                  {...register("act")}
                  style={errors.act && { border: "1px solid red" }}
                />
              </div>
            </DropCard>
            <div className="w-full md:hidden">
              <DropCard title="Gallery" className="py-2">
                <ImagesUpload
                  maxFiles={8}
                  onChange={(files: any) => setValue("gallery", files)}
                  defaultFiles={gallery_images}
                  onDelete={(file, index) => handleRemoveFile(file, index)}
                  accept="image/*, video/*"
                />
              </DropCard>
            </div>
            <DropCard title="Stats & Recognition" className="py-2">
              <textarea
                placeholder="Enter text here..."
                className="px-[18px] py-2 lg:py-3 rounded-xl lg:text-lg w-full text-black border border-slate-300"
                {...register("recognition")}
                style={errors.recognition && { border: "1px solid red" }}
              />
            </DropCard>
            <DropCard title="College Offers" className="py-2">
              <div className="flex flex-col w-full">
                <label className="text-sm lg:text-lg font-medium text-white ml-2 mb-2">
                  Separate offers by a comma or a semicolon.
                </label>
                <textarea
                  placeholder="Enter text here..."
                  className="px-[18px] py-2 lg:py-3 rounded-xl lg:text-lg w-full text-black border border-slate-300"
                  {...register("offers")}
                  style={errors.offers && { border: "1px solid red" }}
                />
              </div>
            </DropCard>
            <DropCard title="Contact" className="flex flex-col gap-2 py-2">
              <div className="flex flex-col w-full">
                <label className={Styles.label}>High School</label>
                <input
                  placeholder=""
                  type="text"
                  className={Styles.input}
                  {...register("school")}
                  style={errors.school && { border: "1px solid red" }}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>City</label>
                <input
                  placeholder=""
                  type="text"
                  className={Styles.input}
                  {...register("city")}
                  style={errors.city && { border: "1px solid red" }}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>State</label>
                <select
                  className={Styles.input}
                  {...register("state")}
                  style={errors.state && { border: "1px solid red" }}
                >
                  <option>Select</option>
                  {statesOptions.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>Coach</label>
                <select
                  className={Styles.input}
                  {...register("coach")}
                  style={errors.coach && { border: "1px solid red" }}
                >
                  <option value={""}>Select</option>
                  {coachesOptions.map((coach) => (
                    <option key={coach.value} value={coach.value}>
                      {coach.label}
                    </option>
                  ))}
                </select>
              </div>
            </DropCard>
            <DropCard title="Follow me" className="flex flex-col gap-2 py-2">
              <div className="flex flex-col w-full">
                <label className={Styles.label}>X (username)</label>
                <input
                  placeholder="X username"
                  type="text"
                  className={Styles.input}
                  {...register("twitter_id")}
                  style={errors.twitter_id && { border: "1px solid red" }}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className={Styles.label}>Hudl (account number)</label>
                <input
                  placeholder="Hudl account number"
                  type="text"
                  className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
                  {...register("hudl")}
                  style={errors.hudl && { border: "1px solid red" }}
                />
              </div>
            </DropCard>

            {formError && (
              <p className="text-red-500 text-center mt-2 mr-2">{formError}</p>
            )}

            <button
              type="submit"
              className={Styles.button}
              disabled={loading || !user?.id}
            >
              Save Changes
              {loading && <Loader />}
            </button>

            <Logo className="w-48 lg:w-64 mx-auto mt-5 lg:hidden" />
          </form>
        </div>
      </div>
    );
  }
}
